type InnerTokenManager = {
  signOut: () => Promise<void>;
  renewTokens: (isSilent?: boolean) => Promise<string | undefined>;
};

/**
 * This class is designed to be used as a singleton to capture the
 * `signinSilent` and `signOut` functions from react-oidc-context to use
 * outside of a React context (i.e. in redux-saga, to renew the id_token on
 * 401 responses). The underlying UserManager is created by the AuthProvider,
 * so it allows for setting the functions after the object is created.
 *
 * See AuthProvider.tsx to see the implementation details of `renewTokens`
 * and `signOut`.
 */
class TokenManager implements InnerTokenManager {
  private resolveTokenManager: (t: InnerTokenManager) => void = () => undefined;
  private readonly innerTokenManager: Promise<InnerTokenManager>;
  private currentRenewTokensPromise: Promise<string | undefined> | undefined = undefined;

  constructor() {
    // This promise will eventually resolve when `setTokenManagerFunctions` is
    // called in AuthProvider.tsx.
    this.innerTokenManager = new Promise<InnerTokenManager>((resolve) => {
      this.resolveTokenManager = resolve;
    });
  }

  /**
   * Allows setting the functions we get from react-oidc-context when it loads up.
   *
   * @param tokenManager The functions from react-oidc-context to use.
   */
  setTokenManagerFunctions(tokenManager: InnerTokenManager) {
    this.resolveTokenManager(tokenManager);
  }

  /**
   * This method triggers token renewal, but throttled; we only allow one
   * consecutive call to this to happen at any one time. If a call is already
   * in progress, we return the promise from that previous call (they
   * effectively become "subscribed" to the result from that call).
   *
   * We call this method when we get a 401 response from the API, so we need to
   * guard against multiple endpoints failing simultaneously and spamming token
   * renewal.
   *
   * @param isSilent If true, the token will be renewed without
   * showing the loading screen. Defaults to true.
   * @returns A Promise for a new token, whether that was started with this
   * call or not.
   */
  async renewTokens(isSilent = true) {
    if (this.currentRenewTokensPromise) {
      // If there's currently an ongoing renewal, we return the existing promise
      // to "subscribe" the caller to the result.
      return await this.currentRenewTokensPromise;
    }

    const tokenManager = await this.innerTokenManager;
    this.currentRenewTokensPromise = tokenManager.renewTokens(isSilent).finally(() => {
      // Only reset the promise after 3s to prevent accidentally triggering
      // renewal again immediately
      setTimeout(() => {
        this.currentRenewTokensPromise = undefined;
      }, 3000);
    });
    return await this.currentRenewTokensPromise;
  }

  async signOut() {
    const tokenManager = await this.innerTokenManager;
    await tokenManager.signOut();
  }
}

export const TokenManagerSingleton = new TokenManager();
